// src/services/aiAnalysisService.js (全機能統合 最終版)
import { lawDependencies, trizLaws } from '../data/trizLawDependencies';
import { nobishiroAbilitiesData } from '../data/nobishiroAbilitiesData';
import { goalSkillMapping } from '../data/goalSkillMapping';
import { projectCharacteristics } from '../data/projectSkillMapping';
import { goalActionMatrix } from '../data/goalActionMatrix';

// ▼▼▼ 新規追加: 不足していた学習タイプ診断関数 ▼▼▼
export const diagnoseLearningType = (growthPatternProfile) => {
    if (!growthPatternProfile) return 'LATE_BLOOMER'; // デフォルト値
    const confidenceScore = growthPatternProfile.q1 || 3;
    return confidenceScore > 3 ? 'EARLY_BLOOMER' : 'LATE_BLOOMER';
};
// ▲▲▲ ここまで ▲▲▲

export const analyzeLeverageStrategy = (trizLawsMastery) => {
  if (!trizLawsMastery || Object.keys(trizLawsMastery).length === 0) return null;
  const allScores = Object.entries(trizLawsMastery).map(([id, data]) => ({ id, score: data.score }));
  allScores.sort((a, b) => b.score - a.score);
  const topStrength = allScores[0];
  if (!topStrength) return null;
  const strengthInfo = trizLaws[topStrength.id] || { name: '未定義の強み' };
  const enablingLawIds = lawDependencies[topStrength.id] || [];
  if (enablingLawIds.length === 0) {
    return { strength: { ...strengthInfo, ...topStrength }, leveragePoint: null, message: 'あなたの強みは独立して高い価値を発揮します。' };
  }
  let leveragePoint = null;
  let minScore = 6;
  enablingLawIds.forEach(id => {
    const lawScore = trizLawsMastery[id]?.score || 0;
    if (lawScore < minScore) {
      minScore = lawScore;
      leveragePoint = { id, score: lawScore, ...trizLaws[id] };
    }
  });
  return { strength: { ...strengthInfo, ...topStrength }, leveragePoint: leveragePoint };
};

export const generateHybridMission = (trizLawsMastery, portfolio) => {
    if (!trizLawsMastery || !portfolio?.growthGoals) return null;
    const strengths = Object.entries(trizLawsMastery).filter(([, data]) => data.score >= 4.0).sort((a, b) => b[1].score - a[1].score);
    if (strengths.length === 0) return null;
    const topStrengthId = strengths[0][0];
    const weakLaws = Object.entries(trizLawsMastery).filter(([lawId, data]) => data.score < 3.0 && lawId !== topStrengthId).sort((a, b) => a[1].score - b[1].score);
    if (weakLaws.length === 0) return null;
    const growthLawId = weakLaws[0][0];
    const strength = { id: topStrengthId, name: trizLaws[topStrengthId]?.name || `法則${topStrengthId}`, score: trizLawsMastery[topStrengthId]?.score || 0 };
    const newElement = { id: growthLawId, name: trizLaws[growthLawId]?.name || `法則${growthLawId}`, score: trizLawsMastery[growthLawId]?.score || 0 };
    const mission = `あなたの強みである「${strength.name}」に、新しい要素「${newElement.name}」を**加上**しましょう。`;
    return { strength, newElement, mission, actionSteps: [`${strength.name}を活用した${newElement.name}の実践例を1つ考える`] };
};

export const diagnosePlateauArchetype = (profile) => {
    if (!profile || !profile.radarChartData || profile.radarChartData.length === 0) return 'FLEXIBLE_ARTISAN';
    const scores = profile.radarChartData.reduce((acc, item) => { acc[item.details.key] = item.score; return acc; }, {});
    const isThinkingDominant = (scores['O'] || 0) > (scores['S'] || 0);
    const isIntuitionDominant = (scores['I'] || 0) > (scores['O2'] || 0);
    if (isThinkingDominant && isIntuitionDominant) return 'LOGICAL_ARCHITECT';
    if (!isThinkingDominant && !isIntuitionDominant) return 'EMPATHETIC_CONNECTOR';
    if (isThinkingDominant && !isIntuitionDominant) return 'BOLD_PIONEER';
    if (!isThinkingDominant && isIntuitionDominant) return 'FLEXIBLE_ARTISAN';
    return 'LOGICAL_ARCHITECT';
};


// ▼▼▼ 新規追加: 目標に対するパーソナルアクション生成関数 ▼▼▼
export const generateGoalActions = (goal, archetypeKey, learningTypeKey) => {
    if (!goal || !goal.text || !archetypeKey || !learningTypeKey) return [];
    const goalText = goal.text.toLowerCase();
    let targetMatrix = null;
    
    // キーワードに合致するアクション知識ベースを選択
    if (goalText.includes('伝える') || goalText.includes('プレゼン')) {
      targetMatrix = goalActionMatrix.presentation;
    }
    // ... 他のキーワードもここに追加 ...

    if (!targetMatrix) return [];

    // アーキタイプと学習タイプに基づいて、最適なアクションを返す
    const archetypeActions = targetMatrix[archetypeKey] || targetMatrix.default;
    if (typeof archetypeActions === 'object' && !Array.isArray(archetypeActions)) {
      return archetypeActions[learningTypeKey] || targetMatrix.default;
    }
    return archetypeActions;
};
// ▲▲▲ ここまで ▲▲▲


export const generatePersonalizedRoadmap = (goal, profile) => {
  if (!goal || !goal.text || !profile || !profile.trizLawsMastery) return null;
  const goalText = goal.text.toLowerCase();
  let targetMapping = null;
  for (const key in goalSkillMapping) {
    if (new RegExp(key).test(goalText)) {
      targetMapping = goalSkillMapping[key];
      break;
    }
  }
  if (!targetMapping) {
    return { summary: "目標が具体的ではありません。より具体的な目標を設定すると、詳細なロードマップを生成できます。", steps: [] };
  }
  const currentAbilities = nobishiroAbilitiesData.map(ability => {
    const scores = ability.relatedLaws.map(lawId => profile.trizLawsMastery?.[lawId]?.score || 0);
    const averageScore = scores.length > 0 ? scores.reduce((a, b) => a + b, 0) / scores.length : 0;
    return { key: ability.key, name: ability.name, score: averageScore };
  });
  const gaps = targetMapping.requiredAbilities.map(abilityKey => {
    const current = currentAbilities.find(a => a.key === abilityKey);
    return { ...current, gap: 5.0 - (current?.score || 0) };
  }).sort((a, b) => b.gap - a.gap);
  const summary = `目標「${targetMapping.description}」の達成には、特に「${gaps[0].name}」と「${gaps[1].name}」の強化が重要です。あなたの現在の強みを活かしつつ、これらのギャップを埋めるためのロードマップを提案します。`;
  const steps = gaps.slice(0, 3).map((gap, index) => ({
    priority: index + 1,
    title: `【ステップ${index + 1}】${gap.name}の強化`,
    description: `${nobishiroAbilitiesData.find(a => a.key === gap.key)?.description || ''} この能力を伸ばすための具体的なアクションを以下に示します。`,
    actions: [
      `関連書籍「（例）${gap.name}を伸ばす思考法」を読む`,
      `関連するオンラインコースを受講する`,
      `日常業務で「${gap.name}」を意識して実践し、週次で振り返る`,
    ]
  }));
  return { summary, steps };
};

export const analyzeTeamHealth = (userProfiles) => {
  const alerts = [];
  const teamMemberProfiles = Object.values(userProfiles).filter(p => p && p.role !== 'master');
  if (teamMemberProfiles.length < 2) return { alerts: [], successionPlans: [] };
  teamMemberProfiles.forEach(profile => {
    const totalScore = Object.values(profile.trizLawsMastery || {}).reduce((sum, law) => sum + (law.score || 0), 0);
    const averageScore = totalScore / (Object.keys(profile.trizLawsMastery || {}).length || 1);
    if (averageScore < 2.5) {
      alerts.push({
        userId: profile.id,
        userName: profile.username,
        type: 'warning',
        title: 'エンゲージメント低下の可能性',
        message: `${profile.username}さんの全体的な能力スコアが低迷しています。モチベーションに関する課題がある可能性があります。`,
        recommendation: '1on1でキャリアに関する対話の時間を設けることを推奨します。',
      });
    }
  });
  const allAbilities = nobishiroAbilitiesData.map(a => a.key);
  const teamAbilityCoverage = {};
  allAbilities.forEach(key => teamAbilityCoverage[key] = 0);
  teamMemberProfiles.forEach(profile => {
     nobishiroAbilitiesData.forEach(ability => {
        const scores = ability.relatedLaws.map(lawId => profile.trizLawsMastery?.[lawId]?.score || 0);
        const averageScore = scores.length > 0 ? scores.reduce((a, b) => a + b, 0) / scores.length : 0;
        if (averageScore >= 4.0) {
            teamAbilityCoverage[ability.key]++;
        }
    });
  });
  const successionPlans = Object.entries(teamAbilityCoverage)
    .filter(([, count]) => count === 0)
    .map(([abilityKey, ]) => {
        const abilityInfo = nobishiroAbilitiesData.find(a => a.key === abilityKey);
        return {
            type: 'gap',
            title: `スキルギャップ警告：${abilityInfo.name}`,
            message: `現在チームには「${abilityInfo.name}」に秀でたメンバーがいません。将来的なリスクとなる可能性があります。`,
            recommendation: `この領域にポテンシャルのあるメンバーを特定し、育成計画を開始することを推奨します。`
        }
    });
  return { alerts, successionPlans };
};

export const simulateProjectSuccess = (selectedCharacteristics, teamMemberProfiles) => {
  if (!selectedCharacteristics || selectedCharacteristics.length === 0 || !teamMemberProfiles || teamMemberProfiles.length === 0) {
    return null;
  }

  // 1. プロジェクト特性から必要能力を集計
  const requiredAbilities = {};
  selectedCharacteristics.forEach(charId => {
    const charData = projectCharacteristics.find(c => c.id === charId);
    if (charData) {
      for (const [abilityKey, weight] of Object.entries(charData.relatedAbilities)) {
        requiredAbilities[abilityKey] = (requiredAbilities[abilityKey] || 0) + weight;
      }
    }
  });

  // 2. チームスコア算出
  const teamScores = {};
  nobishiroAbilitiesData.forEach(ability => {
    let totalScore = 0;
    teamMemberProfiles.forEach(profile => {
      const scores = ability.relatedLaws.map(lawId => profile.trizLawsMastery?.[lawId]?.score || 0);
      const avg = scores.length > 0 ? scores.reduce((a, b) => a + b, 0) / scores.length : 0;
      totalScore += avg;
    });
    teamScores[ability.key] = teamMemberProfiles.length > 0 ? totalScore / teamMemberProfiles.length : 0;
  });

  // 3. 加重スコア計算
  let weightedScore = 0;
  let totalWeight = 0;
  for (const [abilityKey, weight] of Object.entries(requiredAbilities)) {
    weightedScore += (teamScores[abilityKey] || 0) * weight;
    totalWeight += weight;
  }
  const baseSuccessScore = totalWeight > 0 ? (weightedScore / totalWeight) / 5 * 100 : 50;

  // 4. 認知特性多様性ボーナス
  const cognition = { divergent: 0, convergent: 0, holistic: 0, sequential: 0 };
  teamMemberProfiles.forEach(p => {
    const ts = p.neurodiversityProfile?.thinkingStyle?.value || 50;
    const ps = p.neurodiversityProfile?.processingStyle?.value || 50;
    if (ts > 60) cognition.divergent++;
    else if (ts < 40) cognition.convergent++;
    if (ps > 60) cognition.holistic++;
    else if (ps < 40) cognition.sequential++;
  });
  let diversityBonus = 0;
  if (cognition.divergent > 0 && cognition.convergent > 0) diversityBonus += 5;
  else diversityBonus -= 3;
  if (cognition.holistic > 0 && cognition.sequential > 0) diversityBonus += 5;
  else diversityBonus -= 3;

  // 5. チームサイズボーナス（4人チームが最適）
  const sizeBonus = teamMemberProfiles.length === 4 ? 5 : teamMemberProfiles.length >= 2 ? 2 : -5;

  const finalSuccessScore = Math.max(5, Math.min(98, baseSuccessScore + diversityBonus + sizeBonus));

  // 6. リスク・対策生成
  const risks = [];
  const mitigations = [];
  const sortedGaps = Object.entries(requiredAbilities)
    .map(([key, weight]) => ({ key, weight, teamScore: teamScores[key] || 0, gap: weight * (5 - (teamScores[key] || 0)) }))
    .sort((a, b) => b.gap - a.gap);

  sortedGaps.slice(0, 3).forEach(gap => {
    if (gap.gap > 0.8) {
      const ability = nobishiroAbilitiesData.find(a => a.key === gap.key);
      if (ability) {
        risks.push(`「${ability.name}」がプロジェクト要求に対して不足しています（チームスコア: ${(gap.teamScore).toFixed(1)}/5.0）`);
        mitigations.push(`「${ability.name}」に強いメンバーを追加するか、チームで集中的に学習してください`);
      }
    }
  });

  if (cognition.divergent === 0 && selectedCharacteristics.includes('innovation')) {
    risks.push('発散的思考を持つメンバーがおらず、アイデア創出フェーズで苦戦する可能性があります');
    mitigations.push('チーム外からアイデアマンを壁打ち相手として招待することを検討してください');
  }
  if (teamMemberProfiles.length < 2) {
    risks.push('チームメンバーが少なく、単一障害点となるリスクがあります');
    mitigations.push('最低2名以上でチームを構成することを推奨します');
  }

  // 7. メンバー別スコア
  const memberBreakdown = teamMemberProfiles.map(profile => {
    const scores = {};
    nobishiroAbilitiesData.forEach(ability => {
      const lawScores = ability.relatedLaws.map(lawId => profile.trizLawsMastery?.[lawId]?.score || 0);
      scores[ability.key] = lawScores.length > 0 ? lawScores.reduce((a, b) => a + b, 0) / lawScores.length : 0;
    });
    // プロジェクトへの貢献度
    let contribution = 0;
    for (const [key, weight] of Object.entries(requiredAbilities)) {
      contribution += (scores[key] || 0) * weight;
    }
    const contribPct = totalWeight > 0 ? Math.round((contribution / totalWeight) / 5 * 100) : 0;
    return {
      id: profile.id,
      name: profile.displayName || profile.username || '未設定',
      contribution: contribPct,
      topAbility: Object.entries(scores).sort((a, b) => b[1] - a[1])[0]?.[0] || '',
      scores,
    };
  });

  // 8. アクションプラン生成
  const actionPlan = [];
  const score = Math.round(finalSuccessScore);
  if (score < 50) {
    actionPlan.push({ phase: '即時対応（〜1週間）', actions: ['チーム編成の見直し・メンバー追加を検討', '不足能力の集中トレーニング計画を策定', '外部アドバイザーの招聘を検討'] });
    actionPlan.push({ phase: '短期対応（〜1ヶ月）', actions: ['パイロットプロジェクトで小さく試す', 'リスクの高い要素を優先的に解消', 'ステークホルダーへの期待値調整'] });
  } else if (score < 75) {
    actionPlan.push({ phase: '即時対応（〜1週間）', actions: ['不足しているトップ能力の担当者を明確化', 'チーム内ロール分担の再確認', 'リスク管理プランの作成'] });
    actionPlan.push({ phase: '短期対応（〜1ヶ月）', actions: ['定期的な進捗チェックポイントを設置', 'チーム内ナレッジシェアの仕組み化', '外部リソースの活用検討'] });
  } else {
    actionPlan.push({ phase: '即時対応（〜1週間）', actions: ['チームの強みを活かす役割分担の最適化', 'キックオフミーティングで成功イメージを共有', '早期達成可能なクイックウィンを設定'] });
    actionPlan.push({ phase: '短期対応（〜1ヶ月）', actions: ['定期的な振り返りとナレッジ蓄積', '成功体験の可視化とチームモチベーション維持', '次フェーズの先行準備'] });
  }
  actionPlan.push({ phase: '継続的対応', actions: ['月次での成功確率の再評価', 'チームメンバーのスキル向上の継続', 'フィードバックループの確立'] });

  // 9. 期間・マイルストーン予測
  const charData = selectedCharacteristics.map(id => projectCharacteristics.find(c => c.id === id)).filter(Boolean);
  const baseDuration = Math.max(...charData.map(c => c.baseDuration || 6));
  const adjustedDuration = Math.round(baseDuration * (score >= 75 ? 1.0 : score >= 50 ? 1.3 : 1.6));
  const milestones = charData[0]?.milestones || ['計画', '実行', '完了'];
  const milestoneSchedule = milestones.map((name, i) => ({
    name,
    week: Math.round((i + 1) / milestones.length * adjustedDuration * 4),
    month: Math.round(((i + 1) / milestones.length) * adjustedDuration),
  }));

  // 10. 能力ギャップレーダー用データ
  const radarData = Object.entries(requiredAbilities).map(([key, required]) => {
    const ability = nobishiroAbilitiesData.find(a => a.key === key);
    return {
      key,
      name: ability?.name || key,
      required: Math.min(5, required * 5),
      actual: teamScores[key] || 0,
    };
  });

  return {
    score: Math.round(finalSuccessScore),
    risks: risks.length > 0 ? risks : ['特筆すべきリスクは検出されませんでした'],
    mitigations: mitigations.length > 0 ? mitigations : ['現在のチーム構成で良好な成果が期待できます'],
    memberBreakdown,
    actionPlan,
    milestoneSchedule,
    adjustedDuration,
    radarData,
    cognition,
    diversityBonus: diversityBonus > 0 ? diversityBonus : 0,
  };
};
